import React, { useCallback, useEffect, useState } from 'react';
import {
  eventsApi, EVENT_CATEGORIES, EVENT_RECURRENCES,
  CATEGORY_LABEL, RECURRENCE_LABEL,
} from '../../services/eventsApi';
import { getToken } from '../auth';
import './admin-form.css';

const EMPTY_FORM = {
  title: '', slug: '', summary: '', content: '',
  category: 'autre', recurrence: 'unique',
  startDate: '', startTime: '10:00',
  endDate: '',   endTime: '',
  location: '', locationDetails: '',
  organizer: '', contactEmail: '', contactPhone: '',
  price: '', registrationRequired: false, registrationUrl: '',
  imageSeed: '', imageUrl: '',
  featured: false, published: true,
};

function fmtDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' });
}
function fmtDateTime(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  return `${d.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' })} · ${d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}`;
}
function toDateInput(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toISOString().slice(0, 10);
}
function toTimeInput(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toISOString().slice(11, 16);
}
function combineDateTime(date, time) {
  if (!date) return null;
  const t = time || '00:00';
  return new Date(`${date}T${t}:00`).toISOString();
}

const Agenda = () => {
  const token = getToken();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterTime, setFilterTime] = useState('upcoming'); // upcoming | all
  const [query, setQuery] = useState('');

  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError(null);
    try {
      const data = await eventsApi.adminList(token, {
        category: filterCategory !== 'all' ? filterCategory : undefined,
        upcoming: filterTime === 'upcoming' ? true : false,
        q: query || undefined,
        limit: 200,
      });
      setItems(data.items);
      setTotal(data.total);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [token, filterCategory, filterTime, query]);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setEditingId(null); setForm(EMPTY_FORM); setSaveError(null); setOpen(true); };

  const openEdit = async (id) => {
    setEditingId(id);
    setSaveError(null);
    try {
      const { event } = await eventsApi.adminGet(token, id);
      setForm({
        title: event.title,
        slug: event.slug,
        summary: event.summary || '',
        content: event.content || '',
        category: event.category,
        recurrence: event.recurrence,
        startDate: toDateInput(event.startDate),
        startTime: toTimeInput(event.startDate),
        endDate: toDateInput(event.endDate),
        endTime: toTimeInput(event.endDate),
        location: event.location || '',
        locationDetails: event.locationDetails || '',
        organizer: event.organizer || '',
        contactEmail: event.contactEmail || '',
        contactPhone: event.contactPhone || '',
        price: event.price || '',
        registrationRequired: event.registrationRequired,
        registrationUrl: event.registrationUrl || '',
        imageSeed: event.imageSeed || '',
        imageUrl: event.imageUrl || '',
        featured: event.featured,
        published: event.published,
      });
      setOpen(true);
    } catch (err) {
      alert("Impossible de charger l'événement : " + err.message);
    }
  };

  const close = useCallback(() => {
    setOpen(false); setEditingId(null); setForm(EMPTY_FORM); setSaveError(null);
  }, []);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') close(); };
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKey);
    return () => { document.body.style.overflow = ''; window.removeEventListener('keydown', onKey); };
  }, [open, close]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setSaveError(null);
    try {
      const payload = {
        title: form.title,
        slug: form.slug || undefined,
        summary: form.summary,
        content: form.content,
        category: form.category,
        recurrence: form.recurrence,
        startDate: combineDateTime(form.startDate, form.startTime),
        endDate: form.endDate ? combineDateTime(form.endDate, form.endTime || form.startTime) : null,
        location: form.location,
        locationDetails: form.locationDetails,
        organizer: form.organizer,
        contactEmail: form.contactEmail,
        contactPhone: form.contactPhone,
        price: form.price,
        registrationRequired: form.registrationRequired,
        registrationUrl: form.registrationUrl,
        imageSeed: form.imageSeed,
        imageUrl: form.imageUrl,
        featured: form.featured,
        published: form.published,
      };
      if (editingId) await eventsApi.adminUpdate(token, editingId, payload);
      else           await eventsApi.adminCreate(token, payload);
      close();
      await load();
    } catch (err) {
      setSaveError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const onDelete = async (id, title) => {
    if (!window.confirm(`Supprimer définitivement « ${title} » ?`)) return;
    try {
      await eventsApi.adminDelete(token, id);
      await load();
    } catch (err) {
      alert('Suppression impossible : ' + err.message);
    }
  };

  const onToggleFeatured  = async (id, v) => { try { await eventsApi.adminUpdate(token, id, { featured:  !v }); load(); } catch (e) { alert(e.message); } };
  const onTogglePublished = async (id, v) => { try { await eventsApi.adminUpdate(token, id, { published: !v }); load(); } catch (e) { alert(e.message); } };

  if (!token) {
    return (
      <div className="content" style={{ padding: 40 }}>
        <p style={{ fontFamily: 'var(--mono)', fontSize: 12, letterSpacing: '.18em', color: 'var(--char-500)' }}>
          Session expirée. Reconnectez-vous.
        </p>
      </div>
    );
  }

  const catPillClass = (cat) => {
    if (cat === 'religieux') return 'gold';
    if (cat === 'culturel') return 'gold';
    if (cat === 'sportif') return 'sunset';
    if (cat === 'communautaire') return 'ocean';
    if (cat === 'municipal') return '';
    return '';
  };

  return (
    <>
      <div className="topbar">
        <div className="breadcrumb">
          <span>Cambérène</span><span className="sep">/</span>
          <span>Admin</span><span className="sep">/</span>
          <b>Agenda</b>
        </div>
        <div className="search">
          <span className="ic" />
          <input
            type="text"
            placeholder="Chercher un événement…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          <kbd>⌘ K</kbd>
        </div>
        <div className="top-actions">
          <button className="icon-btn" title="Rafraîchir" onClick={load} aria-label="Rafraîchir">↻</button>
          <button className="top-cta" onClick={openCreate}>
            <span className="plus" /> Nouvel événement
          </button>
        </div>
      </div>

      <div className="content">
        <div className="page-head">
          <div>
            <span style={{ fontFamily: 'var(--mono)', fontSize: 11, letterSpacing: '.22em', textTransform: 'uppercase', color: 'var(--char-500)' }}>
              Agenda · {total} événement{total > 1 ? 's' : ''}
            </span>
            <h1>L'agenda,<br /><span style={{ fontStyle: 'italic' }}>en gestion.</span></h1>
            <p className="sub">Niani, Mawlid, matchs, séances du conseil — tous les événements du village. Synchronisé avec /programme-niane.</p>
          </div>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
            <div className="seg" style={{ padding: 3 }}>
              <button className={filterTime === 'upcoming' ? 'on' : ''} onClick={() => setFilterTime('upcoming')}>À venir</button>
              <button className={filterTime === 'all' ? 'on' : ''} onClick={() => setFilterTime('all')}>Tout</button>
            </div>
            <select
              value={filterCategory}
              onChange={(e) => setFilterCategory(e.target.value)}
              className="pill-btn"
              style={{ background: 'var(--bone)', border: '1px solid var(--line)', padding: '7px 14px' }}
            >
              {EVENT_CATEGORIES.map((c) => <option key={c.id} value={c.id}>{c.lbl}</option>)}
            </select>
          </div>
        </div>

        {error && (
          <div className="bulkbar on" style={{ background: 'rgba(230,57,70,.12)', color: '#B72333' }}>
            <span>⚠</span><span>Erreur : {error}</span>
          </div>
        )}

        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>Événement</th>
                <th>Date</th>
                <th>Catégorie</th>
                <th>Récurrence</th>
                <th>Lieu</th>
                <th>Statut</th>
                <th style={{ width: 110 }} />
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={7} style={{ textAlign: 'center', padding: '40px 0', color: 'var(--char-500)', fontFamily: 'var(--mono)', fontSize: 12, letterSpacing: '.18em', textTransform: 'uppercase' }}>Chargement…</td></tr>
              )}
              {!loading && items.length === 0 && (
                <tr><td colSpan={7} style={{ textAlign: 'center', padding: '40px 0', color: 'var(--char-500)' }}>
                  Aucun événement. Cliquez « Nouvel événement » pour démarrer.
                </td></tr>
              )}
              {items.map((e) => (
                <tr key={e.id} onClick={() => openEdit(e.id)} style={e.isPast ? { opacity: 0.5 } : null}>
                  <td>
                    <b style={{ fontWeight: 500, fontSize: 14 }}>
                      {e.title}
                      {e.featured && <span style={{ marginLeft: 6, color: 'var(--gold)' }}>★</span>}
                      {e.isOngoing && <span style={{ marginLeft: 6, color: '#1F7A3A', fontSize: 11, fontFamily: 'var(--mono)', letterSpacing: '.14em' }}>● EN COURS</span>}
                    </b>
                    {e.summary && (
                      <small style={{ display: 'block', marginTop: 3, fontSize: 12, color: 'var(--char-500)', maxWidth: 380, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {e.summary}
                      </small>
                    )}
                  </td>
                  <td style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--char-700)', letterSpacing: '.04em' }}>
                    {fmtDateTime(e.startDate)}
                    {e.endDate && new Date(e.endDate).toDateString() !== new Date(e.startDate).toDateString() && (
                      <small style={{ display: 'block', marginTop: 2, color: 'var(--char-500)' }}>→ {fmtDate(e.endDate)}</small>
                    )}
                  </td>
                  <td><span className={`cat-pill ${catPillClass(e.category)}`}>{CATEGORY_LABEL[e.category] || e.category}</span></td>
                  <td style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--char-500)' }}>
                    {e.recurrence === 'unique' ? '—' : `↻ ${RECURRENCE_LABEL[e.recurrence] || e.recurrence}`}
                  </td>
                  <td style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--char-500)' }}>{e.location || '—'}</td>
                  <td onClick={(ev) => ev.stopPropagation()}>
                    <span
                      className={`status-pill ${e.published ? 'live' : 'muted'}`}
                      style={{ cursor: 'pointer' }}
                      onClick={() => onTogglePublished(e.id, e.published)}
                    >
                      {e.published ? 'Publié' : 'Caché'}
                    </span>
                  </td>
                  <td onClick={(ev) => ev.stopPropagation()}>
                    <div className="row-actions" style={{ opacity: 1 }}>
                      <button
                        title={e.featured ? 'Retirer à la une' : 'Mettre à la une'}
                        onClick={() => onToggleFeatured(e.id, e.featured)}
                        style={{
                          background: e.featured ? 'var(--gold)' : undefined,
                          color: e.featured ? 'var(--char-900)' : undefined,
                          borderColor: e.featured ? 'var(--gold)' : undefined,
                        }}
                      >★</button>
                      <button title="Éditer" onClick={() => openEdit(e.id)}>✎</button>
                      <button title="Supprimer" className="danger" onClick={() => onDelete(e.id, e.title)}>✕</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="table-foot">
            <span>{total} événement{total > 1 ? 's' : ''}</span>
            <span>{items.length} affiché{items.length > 1 ? 's' : ''}</span>
          </div>
        </div>
      </div>

      {/* ============ MODALE ============ */}
      {open && (
        <div className="admin-modal-bg" onClick={close}>
          <div className="admin-modal" onClick={(ev) => ev.stopPropagation()}>
            <div className="admin-modal-head">
              <div>
                <span className="admin-modal-eyebrow">{editingId ? "Éditer l'événement" : 'Nouvel événement'}</span>
                <h2 className="admin-modal-title">{editingId ? form.title || '—' : 'Programmer un événement'}</h2>
              </div>
              <button onClick={close} className="admin-modal-close" aria-label="Fermer">✕</button>
            </div>

            <form onSubmit={onSubmit} className="admin-form">
              <Field label="Titre de l'événement">
                <input
                  type="text"
                  required
                  value={form.title}
                  onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
                  placeholder="Ex : Niani — Le Grand Appel"
                />
              </Field>

              <div className="admin-form-row" style={{ gridTemplateColumns: '1.5fr 1fr' }}>
                <Field label="Slug (URL · optionnel)">
                  <input
                    type="text"
                    value={form.slug}
                    onChange={(e) => setForm((f) => ({ ...f, slug: e.target.value }))}
                    placeholder="auto-généré depuis le titre"
                    pattern="[a-z0-9\-]*"
                  />
                </Field>
                <Field label="Catégorie">
                  <select
                    required
                    value={form.category}
                    onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))}
                  >
                    {EVENT_CATEGORIES.filter((c) => c.id !== 'all').map((c) =>
                      <option key={c.id} value={c.id}>{c.lbl}</option>
                    )}
                  </select>
                </Field>
              </div>

              <Field label="Résumé (1-2 phrases · affiché sur la carte)">
                <textarea
                  rows={2}
                  maxLength={240}
                  value={form.summary}
                  onChange={(e) => setForm((f) => ({ ...f, summary: e.target.value }))}
                  placeholder="Ce qui se passe, en deux phrases d'accroche"
                />
              </Field>

              <Field label="Description longue (page détail)">
                <textarea
                  rows={5}
                  value={form.content}
                  onChange={(e) => setForm((f) => ({ ...f, content: e.target.value }))}
                  placeholder="Programme détaillé, horaires, intervenants…"
                />
              </Field>

              <div className="admin-form-row" style={{ gridTemplateColumns: '1.6fr 1fr 1fr' }}>
                <Field label="Récurrence">
                  <select
                    value={form.recurrence}
                    onChange={(e) => setForm((f) => ({ ...f, recurrence: e.target.value }))}
                  >
                    {EVENT_RECURRENCES.map((r) => <option key={r.id} value={r.id}>{r.lbl}</option>)}
                  </select>
                </Field>
                <Field label="Date de début">
                  <input
                    type="date"
                    required
                    value={form.startDate}
                    onChange={(e) => setForm((f) => ({ ...f, startDate: e.target.value }))}
                  />
                </Field>
                <Field label="Heure de début">
                  <input
                    type="time"
                    value={form.startTime}
                    onChange={(e) => setForm((f) => ({ ...f, startTime: e.target.value }))}
                  />
                </Field>
              </div>

              <div className="admin-form-row">
                <Field label="Date de fin (multi-jour · optionnel)">
                  <input
                    type="date"
                    value={form.endDate}
                    onChange={(e) => setForm((f) => ({ ...f, endDate: e.target.value }))}
                  />
                </Field>
                <Field label="Heure de fin (optionnel)">
                  <input
                    type="time"
                    value={form.endTime}
                    onChange={(e) => setForm((f) => ({ ...f, endTime: e.target.value }))}
                  />
                </Field>
              </div>

              <Field label="Lieu (court · affiché en tête)">
                <input
                  type="text"
                  value={form.location}
                  onChange={(e) => setForm((f) => ({ ...f, location: e.target.value }))}
                  placeholder="Ex : Grande mosquée Layène · Cambérène"
                />
              </Field>

              <Field label="Détails du lieu (adresse complète · optionnel)">
                <input
                  type="text"
                  value={form.locationDetails}
                  onChange={(e) => setForm((f) => ({ ...f, locationDetails: e.target.value }))}
                  placeholder="Rue 8, Cambérène Centre"
                />
              </Field>

              <div className="admin-form-row">
                <Field label="Organisateur">
                  <input
                    type="text"
                    value={form.organizer}
                    onChange={(e) => setForm((f) => ({ ...f, organizer: e.target.value }))}
                    placeholder="Mairie, Comité Layène, ASC…"
                  />
                </Field>
                <Field label="Tarif (texte libre)">
                  <input
                    type="text"
                    value={form.price}
                    onChange={(e) => setForm((f) => ({ ...f, price: e.target.value }))}
                    placeholder="Gratuit · ouvert à tous"
                  />
                </Field>
              </div>

              <div className="admin-form-row">
                <Field label="Email contact">
                  <input
                    type="email"
                    value={form.contactEmail}
                    onChange={(e) => setForm((f) => ({ ...f, contactEmail: e.target.value }))}
                  />
                </Field>
                <Field label="Téléphone contact">
                  <input
                    type="text"
                    value={form.contactPhone}
                    onChange={(e) => setForm((f) => ({ ...f, contactPhone: e.target.value }))}
                  />
                </Field>
              </div>

              <label className="admin-publish-toggle">
                <input
                  type="checkbox"
                  checked={form.registrationRequired}
                  onChange={(e) => setForm((f) => ({ ...f, registrationRequired: e.target.checked }))}
                />
                <span>Inscription requise</span>
              </label>

              {form.registrationRequired && (
                <Field label="URL d'inscription">
                  <input
                    type="url"
                    value={form.registrationUrl}
                    onChange={(e) => setForm((f) => ({ ...f, registrationUrl: e.target.value }))}
                    placeholder="https://…"
                  />
                </Field>
              )}

              <Field label="Image · seed Picsum ou URL">
                <input
                  type="text"
                  value={form.imageSeed || form.imageUrl}
                  onChange={(e) => {
                    const v = e.target.value;
                    if (/^https?:\/\//.test(v)) setForm((f) => ({ ...f, imageUrl: v, imageSeed: '' }));
                    else setForm((f) => ({ ...f, imageSeed: v, imageUrl: '' }));
                  }}
                  placeholder="cb-evt-niani-procession  ·  ou  https://…"
                />
              </Field>

              <div className="admin-form-row">
                <label className="admin-publish-toggle">
                  <input
                    type="checkbox"
                    checked={form.featured}
                    onChange={(e) => setForm((f) => ({ ...f, featured: e.target.checked }))}
                  />
                  <span>★ À la une (mis en avant)</span>
                </label>
                <label className="admin-publish-toggle">
                  <input
                    type="checkbox"
                    checked={form.published}
                    onChange={(e) => setForm((f) => ({ ...f, published: e.target.checked }))}
                  />
                  <span>{form.published ? 'Visible sur l\'agenda public' : 'Caché (brouillon)'}</span>
                </label>
              </div>

              {saveError && <div className="admin-form-error">{saveError}</div>}

              <div className="admin-form-foot">
                <button type="button" onClick={close} className="admin-btn">Annuler</button>
                <button type="submit" disabled={saving} className="admin-btn admin-btn-primary">
                  {saving ? 'Enregistrement…' : (editingId ? 'Enregistrer' : 'Programmer')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
};

function Field({ label, children }) {
  return (
    <label className="admin-field">
      <span className="admin-field-label">{label}</span>
      {children}
    </label>
  );
}

export default Agenda;
